import {
  AfterViewInit,
  Component,
  ComponentRef,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { Select } from "@ngxs/store";
import { combineLatest, Observable, Subject } from "rxjs";
import { delay, distinctUntilChanged, filter, take, takeUntil } from "rxjs/operators";
import { AppState } from "src/app/app.state";
import { LanguageDictionary } from "src/app/models/language-dictionary.model";
import { AfterWizardActionsService } from "src/app/services/after-wizard-actions.service";
import { GlobalApiCallsService } from "src/app/services/global-api-calls.service";
import { LanguageDictionaryService } from "src/app/services/language-dictionary/language-dictionary.service";
import { RulesService } from "src/app/services/rules/rules.service";
import { ZonesService } from "src/app/services/zones.service";
import { isDefined } from "src/utils/isDefined/isDefined";
import { WizardId, WizardService } from "./services/wizard/wizard.service";
import { wizardConfig } from "./wizards/config/config";
import { ValidatedElement } from "./wizards/config/validated-element";
import { ValidatedStep } from "./wizards/config/validated-step";

export interface WizardConfigStep {
	label: string;
	component: any;
	noResetBtn?: boolean;
	getValidatedElements?: (wizardService: WizardService) => ValidatedElement[];
	getPayload?: (wizardService: WizardService) => unknown;
	data?: any;
}

export interface WizardConfig {
	wizardId: string;
	title: string;
	backToRoute: string;
	steps: WizardConfigStep[];
	init?: (
		wizardService: WizardService,
		globalApiCallsService: GlobalApiCallsService,
		initArgs?: {
			wizardId: string;
			data: unknown;
			companyId$?: Observable<string>;
			languageDictionary$?: Observable<LanguageDictionary>;
			languageDictionaryService: LanguageDictionaryService;
			rulesService: RulesService;
		},
	) => Observable<void>;
}

@Component({
	selector: "app-wizard",
	templateUrl: "./wizard.component.html",
	styleUrls: ["./wizard.component.scss"],
})
export class WizardComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
	@Input() wizardId: WizardId = WizardId.createRule;
	@Input() data: unknown;
	@Output() cancelled = new EventEmitter();
	@Output() saveCompleted = new EventEmitter();

	@ViewChild("placeholder", { read: ViewContainerRef, static: true })
	placeholderRef!: ViewContainerRef;

	@ViewChild("navContainer") navContainer!: ElementRef<HTMLElement>;

	@Select(AppState.getSelectedCompanyId)
	selectedCompanyId$: Observable<string>;
	@Select(AppState.selectLanguageDictionary) languageDictionary$: Observable<LanguageDictionary>;

	config: WizardConfig = wizardConfig.find(config => config.wizardId === this.wizardId);

	title: string;
	backToRoute: string;

	buttonStyle = `
  width: 90px !important;
  padding: 0px !important;
  height: 40px !important;
  font-size: 22px !important`;

	stepLabel: string;

	disableNextButton = false;
	disableSaveButton = false;

	destroy$ = new Subject<void>();
	currentStep = 0;

	resetSubjects: Record<number, Subject<void>> = {};

	loaded = false;
	hasError = false;

	private currentComponentRef: ComponentRef<any> | null = null;
	private previouslyFocusedElement: HTMLElement | null = null;

	constructor(
		private wizardService: WizardService,
		private globalApiCallsService: GlobalApiCallsService,
		private languageDictionaryService: LanguageDictionaryService,
		private zonesService: ZonesService,
		private afterWizSvc: AfterWizardActionsService,
		private rulesSvc: RulesService,
	) {}

	async ngOnInit() {
		this.previouslyFocusedElement = document.activeElement as HTMLElement;

		this.rulesSvc.savedRule$.next(null);
		this.zonesService.divisionId$.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(divId => {
			this.disableNextButton = !divId;
		});

		// if we did not come here from edit, clear out any potential zones that would have been emitted
		this.rulesSvc.ruleToPatch$.pipe(takeUntil(this.destroy$)).subscribe(rule => {
			if (!rule) {
				this.zonesService.reset(false);
			}
		});
		this.wizardService.init(this.wizardId);
		this.title = this.config.title;
		this.backToRoute = this.config.backToRoute;

		// listen for errors
		this.wizardService
			.listenToHasError(this.wizardId)
			.pipe(takeUntil(this.destroy$))
			.subscribe(hasError => {
				this.hasError = hasError;
			});

		// listen for title change
		this.wizardService
			.listenToWizardTitleChange(this.wizardId)
			.pipe(takeUntil(this.destroy$))
			.subscribe(title => {
				this.title = title;
			});
		// listen for step changes
		this.wizardService
			.listenToStepChanges(this.wizardId)
			.pipe(takeUntil(this.destroy$))
			.subscribe(currentStep => {
				this.currentStep = currentStep;
				this.loadStepComponent();
				this.disableSaveButton = false;
				this.hasError = false;
			});

		// listen for save completed
		this.wizardService
			.listenToSaveCompleted(this.wizardId)
			.pipe(takeUntil(this.destroy$))
			.subscribe(_ => {
				this.afterWizSvc.reset();
				this.saveCompleted.emit();
			});
		// listen to requests to disable the save button
		this.setSaveButton();

		// optional init
		if (typeof this.config.init === "function") {
			this.config
				.init(this.wizardService, this.globalApiCallsService, {
					wizardId: this.wizardId,
					data: this.data,
					companyId$: this.selectedCompanyId$.pipe(filter(isDefined), take(1)),
					languageDictionary$: this.languageDictionary$.pipe(filter(isDefined), take(1)),
					languageDictionaryService: this.languageDictionaryService,
					rulesService: this.rulesSvc,
				})
				.subscribe(() => {
					this.loaded = true;

					this.loadStepComponent();
				});
		} else {
			// Initial component load
			this.loaded = true;

			this.loadStepComponent();
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!changes?.data?.currentValue) {
			if (this.rulesSvc.ruleToPatch$.value) {
				this.data = this.rulesSvc.ruleToPatch$.value;
			}
		}
	}

	ngAfterViewInit(): void {
		setTimeout(() => {
			const steps = this.navContainer?.nativeElement.querySelectorAll<HTMLElement>('[role="button"]');
			if (steps && steps.length > 0) {
				steps[0].focus();
			}
		}, 0);
	}

	setSaveButton() {
		combineLatest([this.wizardService.listenToDisableSaveButton(this.wizardId), this.rulesSvc.disableSaveButton$])
			.pipe(delay(0), takeUntil(this.destroy$))
			.subscribe(([disableSaveButton, disableFromExcludeZonesDupe]) => {
				this.disableSaveButton = disableSaveButton;

				const hasDupeZones =
					typeof disableFromExcludeZonesDupe === "boolean" &&
					!disableSaveButton &&
					disableFromExcludeZonesDupe;
				if (hasDupeZones) {
					this.disableSaveButton = true;
				}
			});
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
		this.currentComponentRef?.destroy();

		if (this.previouslyFocusedElement) {
			this.previouslyFocusedElement.focus();
		}
	}

	onPrev() {
		this.wizardService.decrementCurrentStep(this.wizardId);
	}

	onNext() {
		this.wizardService.incrementCurrentStep(this.wizardId);
	}

	onSave() {
		this.wizardService.saveButtonClicked(this.wizardId);
	}

	onReset() {
		this.resetSubjects[this.currentStep].next();
	}

	onCancel() {
		this.cancelled.emit();
		this.afterWizSvc.reset();
		this.rulesSvc.savedRule$.next(null);
		this.rulesSvc.endInspectionTypes$.next(null);
		this.rulesSvc.startInspectionTypes$.next(null);
	}

	setCurrentStep(currentStep: number) {
		this.wizardService.setCurrentStep(this.wizardId, currentStep);
	}

	private loadStepComponent() {
		if (!this.config?.steps?.[this.currentStep]?.component) return;

		const step = this.config.steps[this.currentStep];
		this.stepLabel = step.label;

		// Clear current component
		this.placeholderRef.clear();

		// Create and store reference
		const cmpRef = this.placeholderRef.createComponent(this.config.steps[this.currentStep].component);

		if (step.data) {
			for (const [key, value] of Object.entries(step.data)) {
				cmpRef.setInput?.(key, value);
			}
		}
		cmpRef.setInput("getStepValidators", this.getValidators.bind(this));
		cmpRef.setInput("getPayloads", this.getPayloads.bind(this));

		this.currentComponentRef = cmpRef;

		// bind the reset Subject
		this.resetSubjects[this.currentStep] = this.resetSubjects[this.currentStep] || new Subject<void>();

		cmpRef.setInput("reset$", this.resetSubjects[this.currentStep].asObservable());
	}

	getValidators(): ValidatedStep[] {
		const validators: ValidatedStep[] = [];

		this.config.steps?.forEach(step => {
			if (step.getValidatedElements) {
				validators.push({
					stepLabel: step.label,
					getValidatedElements: step.getValidatedElements,
				});
			}
		});
		return validators;
	}
	getPayloads(): unknown[] {
		const payloads: ((wizardService: WizardService) => unknown)[] = [];

		this.config.steps?.forEach(step => {
			if (step.getPayload) {
				payloads.push(step.getPayload);
			}
		});

		return payloads;
	}

	/** Keyboard navigation – Up/Down arrows move focus between wizard steps. */
	onNavKeydown(event: KeyboardEvent, navEl: HTMLElement): void {
		if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
			return;
		}

		// Only include enabled steps (tabIndex === 0); disabled steps have tabIndex === -1
		const steps = Array.from(navEl.querySelectorAll<HTMLElement>('[role="button"]')).filter(
			el => el.tabIndex !== -1,
		);
		if (!steps.length) {
			return;
		}

		const focused = document.activeElement as HTMLElement;
		const idx = steps.indexOf(focused);

		if (event.key === "ArrowDown") {
			event.preventDefault();
			const next = idx < steps.length - 1 ? steps[idx + 1] : steps[0];
			next.focus();
		} else if (event.key === "ArrowUp") {
			event.preventDefault();
			const prev = idx > 0 ? steps[idx - 1] : steps[steps.length - 1];
			prev.focus();
		}
	}
}
